<template>
    <chart-template :title="$t('Availability Per Asset')">
        <div class="row charts" :style="{ height: chartHeight }">
            <v-chart
                v-if="comparisonReport"
                class="col"
                :option="comparisonReportOptions"
                autoresize
            />

            <v-chart class="col" :option="reportOptions" autoresize />
        </div>
    </chart-template>
</template>

<script>
import { formatDate } from '@utils/dateRangeFormatter.js';
import { colors } from 'quasar';
import ChartTemplate from './ChartTemplate.vue';
const { getPaletteColor } = colors;

import { BarChart } from 'echarts/charts';
import {
    LegendComponent,
    TitleComponent,
    TooltipComponent
} from 'echarts/components';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import VChart from 'vue-echarts';
import { mapState } from 'pinia';
import { useAuthStore } from '@stores/auth';

use([
    CanvasRenderer,
    BarChart,
    TitleComponent,
    TooltipComponent,
    LegendComponent
]);

export default {
    name: 'AvailabilityChart',
    components: { VChart, ChartTemplate },
    props: {
        report: {
            type: Object,
            required: true
        },
        comparisonReport: {
            type: Object,
            required: false
        }
    },
    computed: {
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        reportOptions() {
            const legends = this.getTitle(
                this.report,
                this.$t('Current Period')
            );
            return this.getChartOptions(this.report, legends);
        },
        comparisonReportOptions() {
            const legends = this.getTitle(
                this.comparisonReport,
                this.$t('Comparison Period')
            );
            return this.getChartOptions(this.comparisonReport, legends);
        },
        /**
         * Computes the height of the chart based on the number of lines in the y-axis data.
         * @returns {string} The height of the chart in pixels.
         */
        chartHeight() {
            const currentReportTotalLines = this.report.rows.length;
            const currentReportHeight = currentReportTotalLines * 30 + 100;

            if (this.comparisonReport) {
                const compReportTotalLines = this.comparisonReport.rows.length;
                const compReportHeight = compReportTotalLines * 30 + 100;

                const heightWithComparison = Math.max(
                    currentReportHeight,
                    compReportHeight
                );

                return heightWithComparison > 600
                    ? `${heightWithComparison}px`
                    : '600px';
            }

            return currentReportHeight > 600
                ? `${currentReportHeight}px`
                : '600px';
        }
    },
    methods: {
        /**
         * Determine y-axis options based on the number of results to plot on the chart.
         * @return {Object}
         */
        yAxisOptions(report) {
            const names = report.rows.map(row => row.name);
            const yAxisOptions = {
                type: 'category',
                axisLabel: {
                    color: '#333',
                    fontSize: 13,
                    interval: 'auto'
                },
                data: names
            };
            return yAxisOptions;
        },
        /**
         * Get chart title if comparison report is available.
         * @param {Object} report
         * @param {String} chartTitle
         * @returns {Object} object containing chart title and subtitle
         */
        getTitle(report, chartTitle) {
            let title = null;
            let subtitle = null;
            if (this.comparisonReport) {
                title = chartTitle;
                subtitle = formatDate(
                    report.dateInterval.start,
                    report.dateInterval.end,
                    this.userLocale
                );
            }
            return { title: title, subtitle: subtitle };
        },
        /**
         * Get the chart definition for the echart component.
         * @param {Object} report object
         * @param {Object} legends object containing chart title and subtitle
         * @returns {Object} full chart options object
         */
        getChartOptions(report, legends) {
            return {
                title: {
                    text: legends.title,
                    textStyle: {
                        fontSize: 14
                    },
                    subtext: legends.subtitle,
                    subtextStyle: {
                        fontSize: 14
                    },
                    bottom: 0,
                    left: 'center'
                },
                grid: {
                    top: 40,
                    bottom: this.comparisonReport ? 80 : 0,
                    outerBoundsMode: 'same',
                    outerBoundsContain: 'axisLabel'
                },
                tooltip: {
                    trigger: 'axis',
                    axisPointer: {
                        type: 'shadow'
                    },
                    formatter: params => {
                        return this.formatTooltipContent(params);
                    }
                },
                legend: {
                    data: [
                        this.$t('Running'),
                        this.$t('Availability loss'),
                        this.$t('Performance loss')
                    ],
                    orient: 'horizontal',
                    top: 5,
                    left: 5
                },
                xAxis: {
                    show: this.$q.screen.gt.xs,
                    type: 'value',
                    name: this.$t('min'),
                    nameLocation: 'middle',
                    nameGap: 30,
                    axisLabel: {
                        fontSize: 13,
                        color: 'black'
                    },
                    nameTextStyle: {
                        fontSize: 13,
                        color: 'black'
                    }
                },
                yAxis: this.yAxisOptions(report),
                series: [
                    {
                        name: this.$t('Running'),
                        type: 'bar',
                        stack: 'total',
                        data: report.rows.map(row =>
                            (row.availability.running / 60).toFixed(2)
                        )
                    },
                    {
                        name: this.$t('Availability loss'),
                        type: 'bar',
                        stack: 'total',
                        data: report.rows.map(row =>
                            (
                                row.availability.availabilityDowntime / 60
                            ).toFixed(2)
                        )
                    },
                    {
                        name: this.$t('Performance loss'),
                        type: 'bar',
                        stack: 'total',
                        data: report.rows.map(row =>
                            (row.availability.performanceDowntime / 60).toFixed(
                                2
                            )
                        )
                    }
                ],
                // order: running, availability downtime, performance downtime
                color: [
                    getPaletteColor('light-green'),
                    getPaletteColor('light-yellow'),
                    getPaletteColor('dark-yellow')
                ]
            };
        },
        /**
         * Format the tooltip content.
         * @param {Array} params
         * @returns {String} formatted tooltip content
         */
        formatTooltipContent(params) {
            if (!Array.isArray(params) || !params.length) {
                return '';
            }
            const assetName = params[0]['axisValueLabel'] ?? '';
            const lines = [`<div>${assetName}</div>`];

            // Helper to render a line
            const renderLine = param => {
                if (!param) {
                    return '';
                }
                const marker = param['marker'] ?? '';
                const label = param['seriesName'] ?? '';
                const value = param['data'] ?? '';
                return `<div class="q-my-sm">${marker}${label}: <b>${value} ${this.$t('min')}</b></div>`;
            };

            // Users can filter the chart by series.
            // Therefore, the params array can have 3, 2 or 1 element.
            for (let i = 0; i < params.length; i++) {
                lines.push(renderLine(params[i]));
            }

            return lines.join('');
        }
    }
};
</script>

<style lang="scss" scoped>
@import './dashboard.scss';
.charts {
    width: 100%;
    padding-bottom: 15px;
}
</style>
